import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { userAPI, authAPI } from "../services/api";
import { PasswordStrength, passwordValid } from "./shared/index";
import { useLanguage } from "../context/LanguageContext";
import { MdBadge, MdPerson, MdEmail, MdPhone, MdSecurity, MdEdit, MdAccountCircle, MdSave, MdVpnKey } from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

const inputStyle = {
  width:"100%", padding:"9px 12px", border:`1px solid ${BORDER}`,
  borderRadius:8, fontSize:13, outline:"none", background:"var(--surface)",
  fontFamily:KH, boxSizing:"border-box",
};

export default function ProfileModal({ onClose, initialTab = "view" }) {
  const { user, login } = useAuth();
  const { t } = useLanguage();
  const [tab,     setTab]     = useState(initialTab);
  const [form,    setForm]    = useState({
    userNameKh: user?.userNameKh || "",
    userNameEn: user?.userNameEn || "",
    email:      user?.email      || "",
    phone:      user?.phone      || "",
  });
  const [passForm, setPassForm] = useState({ oldPassword:"", newPassword:"", confirm:"" });
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState(null);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const p = (k, v) => setPassForm(prev => ({ ...prev, [k]: v }));

  const handleSaveProfile = async () => {
    setSaving(true); setMsg(null);
    try {
      await userAPI.update(user?.id, {
        userNameKh: form.userNameKh,
        userNameEn: form.userNameEn,
        email:      form.email,
        phone:      form.phone,
      });
      const updated = { ...user, userNameKh:form.userNameKh,
                        userNameEn:form.userNameEn, email:form.email };
      localStorage.setItem("user", JSON.stringify(updated));
      setMsg({ type:"success", text:t("រក្សាទុករួចហើយ!") });
      setTab("view");
    } catch (e) {
      setMsg({ type:"error", text:"Error: " + e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passForm.oldPassword || !passForm.newPassword) {
      setMsg({ type:"error", text:t("សូមបំពេញពាក្យសម្ងាត់ទាំងអស់") }); return;
    }
    if (passForm.newPassword !== passForm.confirm) {
      setMsg({ type:"error", text:t("ពាក្យសម្ងាត់ថ្មីមិនត្រូវគ្នា") }); return;
    }
    if (!passwordValid(passForm.newPassword)) {
      setMsg({ type:"error", text:t("ពាក្យសម្ងាត់ថ្មីមិនទាន់គ្រប់លក្ខខណ្ឌ") }); return;
    }
    setSaving(true); setMsg(null);
    try {
      await authAPI.changePassword(user?.id, {
        oldPassword:     passForm.oldPassword,
        newPassword:     passForm.newPassword,
        confirmPassword: passForm.confirm,
      });
      setMsg({ type:"success", text:t("ពាក្យសម្ងាត់ត្រូវបានផ្លាស់ប្តូររួចហើយ!") });
      setPassForm({ oldPassword:"", newPassword:"", confirm:"" });
      setTab("view");
    } catch (e) {
      setMsg({ type:"error", text:"Error: " + e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.5)",
      zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background:"var(--surface)", borderRadius:16, width:460,
        maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto",
        boxShadow:"0 24px 80px rgba(0,0,0,0.3)",
      }}>
        <div style={{ background:BROWN, padding:"24px 24px 20px",
                      borderRadius:"16px 16px 0 0", position:"relative" }}>
          <button onClick={onClose}
            style={{ position:"absolute", top:16, right:16,
                     background:"rgba(255,255,255,0.15)", border:"none",
                     borderRadius:"50%", width:28, height:28,
                     cursor:"pointer", color:"#fff", fontSize:14 }}>×</button>

          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            <div style={{
              width:60, height:60, borderRadius:"50%",
              background:"rgba(196,149,106,0.3)",
              border:"2px solid rgba(196,149,106,0.6)",
              display:"flex", alignItems:"center", justifyContent:"center",
              flexShrink:0, overflow:"hidden",
            }}>
              <MdAccountCircle size={40} color="#F5DEB3" />
            </div>
            <div>
              <p style={{ fontSize:16, fontWeight:700, color:"#fff",
                          fontFamily:MO, marginBottom:4 }}>
                {user?.userNameKh || user?.userNameEn || "អ្នកប្រើប្រាស់"}
              </p>
              <p style={{ fontSize:11, color:"rgba(196,149,106,0.8)", fontFamily:KH }}>
                {user?.roleName || "—"}
              </p>
              <p style={{ fontSize:11, color:"rgba(255,255,255,0.5)", fontFamily:KH }}>
                {user?.email || "—"}
              </p>
            </div>
          </div>

          <div style={{ display:"flex", gap:4, marginTop:20 }}>
            {[
              { key:"view",     label:"ព័ត៌មាន" },
              { key:"edit",     label:"កែប្រែ"  },
              { key:"password", label:"ពាក្យសម្ងាត់" },
            ].map(tb => (
              <button key={tb.key} onClick={() => { setTab(tb.key); setMsg(null); }}
                style={{
                  padding:"6px 16px", borderRadius:20, cursor:"pointer",
                  fontSize:11, fontFamily:KH, border:"none",
                  background: tab===tb.key ? "rgba(255,255,255,0.2)" : "transparent",
                  color: tab===tb.key ? "#fff" : "rgba(255,255,255,0.5)",
                  fontWeight: tab===tb.key ? 600 : 400,
                }}>
                {t(tb.label)}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding:24 }}>
          {msg && (
            <div style={{
              padding:"10px 14px", borderRadius:8, marginBottom:16,
              background: msg.type==="success" ? "#D1FAE5" : "#FEE2E2",
              color:      msg.type==="success" ? "#065F46" : "#991B1B",
              fontSize:13, fontFamily:KH,
            }}>
              {msg.text}
            </div>
          )}

          {tab === "view" && (
            <div>
              {[
                { icon:<MdBadge size={16}/>,    label:"លេខសម្គាល់",     value:user?.id },
                { icon:<MdPerson size={16}/>,   label:"ឈ្មោះខ្មែរ",     value:user?.userNameKh },
                { icon:<MdPerson size={16}/>,   label:"ឈ្មោះអង់គ្លេស",  value:user?.userNameEn },
                { icon:<MdEmail size={16}/>,    label:"អ៊ីមែល",          value:user?.email },
                { icon:<MdPhone size={16}/>,    label:"ទូរស័ព្ទ",        value:user?.phone },
                { icon:<MdSecurity size={16}/>, label:"តួនាទី",          value:user?.roleName },
              ].map((r,i) => (
                <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:12,
                                       padding:"10px 14px", background:BG,
                                       borderRadius:8, marginBottom:6 }}>
                  <span style={{ flexShrink:0, marginTop:2, color:MUTED, display:"flex", alignItems:"center" }}>{r.icon}</span>
                  <div>
                    <p style={{ fontSize:12, color:MUTED, fontFamily:KH,
                                 marginBottom:2 }}>{t(r.label)}</p>
                    <p style={{ fontSize:13, fontWeight:500, color:"var(--text)",
                                 fontFamily:KH }}>{r.value || "—"}</p>
                  </div>
                </div>
              ))}
              <button onClick={() => setTab("edit")}
                style={{ marginTop:16, width:"100%", padding:"10px",
                         background:BROWN, color:"#fff", border:"none",
                         borderRadius:8, cursor:"pointer", fontSize:13, fontFamily:KH }}>
                <MdEdit size={14} style={{marginRight:6}}/> {t("កែប្រែព័ត៌មាន")}
              </button>
            </div>
          )}

          {tab === "edit" && (
            <div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, color:MUTED,
                                marginBottom:4, fontFamily:KH }}>{t("ឈ្មោះខ្មែរ")}</label>
                <input value={form.userNameKh} onChange={e => f("userNameKh",e.target.value)}
                  placeholder={t("ឈ្មោះខ្មែរ...")} style={inputStyle} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, color:MUTED,
                                marginBottom:4, fontFamily:KH }}>{t("ឈ្មោះអង់គ្លេស")}</label>
                <input value={form.userNameEn} onChange={e => f("userNameEn",e.target.value)}
                  placeholder="English name..." style={inputStyle} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, color:MUTED,
                                marginBottom:4, fontFamily:KH }}>{t("អ៊ីមែល")}</label>
                <input type="email" value={form.email} onChange={e => f("email",e.target.value)}
                  placeholder="example@email.com" style={inputStyle} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, color:MUTED,
                                marginBottom:4, fontFamily:KH }}>{t("ទូរស័ព្ទ")}</label>
                <input value={form.phone} onChange={e => f("phone",e.target.value)}
                  placeholder="0xx xxx xxx" style={inputStyle} />
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setTab("view")}
                  style={{ flex:1, padding:"10px", background:BG,
                           border:`1px solid ${BORDER}`, borderRadius:8,
                           cursor:"pointer", fontSize:13, fontFamily:KH }}>
                  {t("បោះបង់")}
                </button>
                <button onClick={handleSaveProfile} disabled={saving}
                  style={{ flex:2, padding:"10px", background:BROWN, color:"#fff",
                           border:"none", borderRadius:8,
                           cursor:saving?"not-allowed":"pointer",
                           fontSize:13, fontFamily:KH, opacity:saving?0.7:1,
                           display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                  {saving ? "កំពុងរក្សាទុក..." : <><MdSave size={15}/> {t("រក្សាទុក")}</>}
                </button>
              </div>
            </div>
          )}

          {tab === "password" && (
            <div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, color:MUTED,
                                marginBottom:4, fontFamily:KH }}>{t("ពាក្យសម្ងាត់បច្ចុប្បន្ន")}</label>
                <input type="password" value={passForm.oldPassword}
                  onChange={e => p("oldPassword",e.target.value)}
                  placeholder={t("ពាក្យសម្ងាត់បច្ចុប្បន្ន...")} style={inputStyle} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, color:MUTED,
                                marginBottom:4, fontFamily:KH }}>{t("ពាក្យសម្ងាត់ថ្មី")}</label>
                <input type="password" value={passForm.newPassword}
                  onChange={e => p("newPassword",e.target.value)}
                  placeholder={t("ពាក្យសម្ងាត់ថ្មី...")}
                  style={{
                    ...inputStyle,
                    ...(passForm.newPassword && !passwordValid(passForm.newPassword)
                      ? { border:"1.5px solid #EF4444", background:"#FFF5F5" }
                      : {}),
                  }} />
                <PasswordStrength password={passForm.newPassword} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:"block", fontSize:12, color:MUTED,
                                marginBottom:4, fontFamily:KH }}>{t("បញ្ជាក់ពាក្យសម្ងាត់ថ្មី")}</label>
                <input type="password" value={passForm.confirm}
                  onChange={e => p("confirm",e.target.value)}
                  placeholder={t("បញ្ជាក់ម្តងទៀត...")} style={inputStyle} />
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setTab("view")}
                  style={{ flex:1, padding:"10px", background:BG,
                           border:`1px solid ${BORDER}`, borderRadius:8,
                           cursor:"pointer", fontSize:13, fontFamily:KH }}>
                  {t("បោះបង់")}
                </button>
                <button onClick={handleChangePassword} disabled={saving}
                  style={{ flex:2, padding:"10px", background:BROWN, color:"#fff",
                           border:"none", borderRadius:8,
                           cursor:saving?"not-allowed":"pointer",
                           fontSize:13, fontFamily:KH, opacity:saving?0.7:1,
                           display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                  {saving ? "កំពុងផ្លាស់ប្តូរ..." : <><MdVpnKey size={15}/> {t("ផ្លាស់ប្តូរ")}</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}